// The web's plain HTTP, only for what is not GraphQL (the kit core is the client for records and questions, kit.ts):
// sign-in at auth (auth:Interface/api; its answers' types generated into auth/contracts/api.ts: /session/*, /sessions,
// /grants, /connections, /tokens, /invites), the sealed store (truss:Interface/secrets: /secrets/v1), blobs, the
// instance's discovery document and its package registry (/packages/index.json).
import { platformSecrets } from "@hq-org/platform-wasm";
import type * as Auth from "@hq-org/contracts/auth";
import { type Api, ApiError, type AppGrant, type Connection, type Login, type Token } from "./types";

export type Conn = {
	base: () => string;
	auth: () => string | null;
	token: () => string | null;
	/** a fresh access token after a 401 (null = signed out) */
	refresh: () => Promise<string | null>;
};

type Raw = Record<string, any>;
/** The platform's client core (platform/bindings/wasm): the kit's engine and the sealed store's client half. */
export const WASM = "/kit/platform_wasm_bg.wasm";

/** An error body's text: `error` may be a string or {message, code}; else the status. */
const message = (j: Raw | null, status: number): string => {
	const e = j?.error;
	if (typeof e === "string") return j?.error_description ?? e;
	if (e && typeof e === "object") return String(e.message ?? e.code ?? JSON.stringify(e));
	return status === 401 ? "unauthorized" : `http ${status}`;
};

/** seconds, milliseconds or an ISO string → ISO (auth answers epoch numbers) */
export const iso = (v: unknown): string | null =>
	typeof v === "number" ? new Date(v < 1e12 ? v * 1000 : v).toISOString() : typeof v === "string" && v ? v : null;
const when = (v: unknown) => iso(v) ?? new Date(0).toISOString();
/** `https://hq-digiterium…`, `hq-auth-x`, `x` → the instance name. */
const nameOf = (v: unknown) => String(v ?? "?").replace(/^https?:\/\//, "").replace(/^hq-(auth-)?/, "").split(/[./]/)[0]!;
/** An incoming grant held by another instance's home (vs an app such as Claude): its client is an hq origin, or it is
 *  an instance this home is connected to. Auth doesn't flag it yet (grantsOf: metadata.home). */
const isHome = (g: Auth.Grant, out: Auth.Connection[]) => /^https?:\/\/hq-/.test(String(g.clientId ?? "")) || out.some((c) => nameOf(c.instance) === nameOf(g.instance));
const strings = (v: unknown): string[] => (Array.isArray(v) && v.every((x) => typeof x === "string") ? v : []);

export function createNet(c: Conn, doFetch: (input: string, init?: RequestInit) => Promise<Response> = (i, o) => fetch(i, o)) {
	const base = () => c.base().replace(/\/+$/, "");

	/** With the bearer; a 401 refreshes once and asks again. */
	const request = async (url: string, init: RequestInit = {}, token = c.token(), retried = false): Promise<Response> => {
		let res: Response;
		try {
			res = await doFetch(url, { ...init, headers: { ...(init.headers as Record<string, string>), ...(token ? { authorization: `Bearer ${token}` } : {}) } });
		} catch {
			throw new ApiError("offline");
		}
		if (res.status === 401 && !retried) {
			const fresh = await c.refresh();
			if (fresh) return request(url, init, fresh, true);
		}
		if (res.status === 401) throw new ApiError("unauthorized", 401);
		return res;
	};
	const json = async <T = Raw>(url: string, method = "GET", body?: unknown): Promise<T> => {
		const res = await request(url, {
			method,
			headers: body === undefined ? {} : { "content-type": "application/json" },
			body: body === undefined ? undefined : JSON.stringify(body),
		});
		if (res.status === 204) return undefined as T;
		const j = (await res.json().catch(() => null)) as Raw | null;
		if (!res.ok) throw new ApiError(message(j, res.status), res.status);
		return j as T;
	};
	/** auth's own routes: the same bearer, auth's origin. */
	const auth = <T = Raw>(path: string, method = "GET", body?: unknown) => {
		const a = c.auth();
		if (!a) return Promise.reject(new ApiError("this instance has no auth service", 501));
		return json<T>(`${a}${path}`, method, body);
	};
	/** Before there is a bearer (or to renew it): explicit URLs, never the app's token. */
	const post = async <T>(url: string, body?: unknown): Promise<T> => {
		let res: Response;
		try {
			res = await doFetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
		} catch {
			throw new ApiError("offline");
		}
		const j = (await res.json().catch(() => null)) as Raw | null;
		if (!res.ok) throw new ApiError(message(j, res.status), res.status);
		return j as T;
	};
	type Answer = Auth.AppSession;
	const login = (a: Answer): Login => ({ access: a.access_token, refresh: a.refresh_token, expiresIn: a.expires_in });

	let known: Promise<{ instance: string | null; auth: string | null }> | null = null;
	let knownFor = "";
	const discover = async (b: string) => {
		let res: Response;
		try {
			res = await doFetch(`${b}/.well-known/truss`);
		} catch {
			throw new ApiError(`${b} does not answer`);
		}
		const k = (res.ok ? await res.json().catch(() => ({})) : {}) as { instance?: string; auth?: string };
		return { instance: k.instance ?? null, auth: k.auth?.replace(/\/+$/, "") || null };
	};
	/** This instance's name (its discovery document, once per base). */
	const instance = async () => {
		if (!known || knownFor !== base()) {
			knownFor = base();
			known = discover(base()).catch(() => ({ instance: null, auth: null }));
		}
		return (await known).instance ?? "";
	};

	/** Connect's way back: Instances (the page under Connect, /settings/instances/connect), without the invite's
	 *  ?connect= origin. */
	const returnTo = () => {
		const u = new URL(window.location.href);
		u.pathname = u.pathname.replace(/\/connect\/?$/, "");
		u.searchParams.delete("connect");
		return u.toString();
	};

	const connections = async (): Promise<Connection[]> => {
		const [out, grants] = await Promise.all([auth<Auth.Connection[]>("/connections"), auth<Auth.Grant[]>("/grants").catch(() => [] as Auth.Grant[])]);
		const inc = grants.filter((g) => isHome(g, out)); // apps are in access.apps()
		return [
			...(out ?? []).map((x) => ({
				id: String(x.id),
				direction: "outgoing" as const,
				instance: nameOf(x.instance),
				url: String(x.resource ?? x.issuer ?? ""),
				person: null,
				scopes: x.scopes ?? [],
				createdAt: when(x.createdAt),
				lastUsedAt: x.updatedAt ? when(x.updatedAt) : null,
				status: x.status === "active" ? ("active" as const) : ("revoked" as const),
			})),
			...(inc ?? []).map((g) => ({
				id: String(g.id),
				direction: "incoming" as const,
				instance: nameOf(g.instance),
				url: String(g.clientId ?? ""),
				person: null,
				scopes: g.scopes ?? [],
				createdAt: when(g.createdAt),
				lastUsedAt: null,
				status: "active" as const,
			})),
		];
	};

	const scope = (project: string) => `${base()}/secrets/v1/scopes/${encodeURIComponent(project)}`;
	const recipients = async (project: string) => strings((await json<Raw>(`${scope(project)}/recipients`)).recipients);
	/** A GET secrets answer (ciphertexts only; each value in it is a read event in the log), as JSON text for the core;
	 *  `refs` empty = the whole scope. The URL names refs, never values. */
	const read = async (project: string, refs: string[] = []) =>
		JSON.stringify(await json(`${scope(project)}/secrets${refs.length ? `?${refs.map((r) => `ref=${encodeURIComponent(r)}`).join("&")}` : ""}`));

	const api = {
		auth: {
			discover,
			login: async (a, pat) => login(await post<Answer>(`${a}/session/login`, { pat })),
			refresh: async (a, refreshToken) => login(await post<Answer>(`${a}/session/refresh`, { refresh_token: refreshToken })),
			async logout(a, refreshToken) {
				await post(`${a}/session/logout`, { refresh_token: refreshToken });
			},
			async browser(a) {
				try {
					return login(await post<Answer>(`${a}/session/browser`));
				} catch {
					return null;
				}
			},
		},
		access: {
			async sessions() {
				const rows = await auth<Auth.SignIn[]>("/sessions");
				return rows.map((r) => ({
					id: String(r.id),
					kind: String(r.kind ?? "app"),
					agent: r.agent ?? null,
					createdAt: when(r.createdAt),
					usedAt: r.usedAt ? when(r.usedAt) : null,
					current: !!r.current,
				}));
			},
			async endSession(id) {
				await auth(`/sessions/${encodeURIComponent(id)}`, "DELETE");
			},
			async endOthers() {
				const rows = await auth<Auth.SignIn[]>("/sessions");
				await Promise.all(rows.filter((r) => !r.current).map((r) => auth(`/sessions/${encodeURIComponent(String(r.id))}`, "DELETE")));
			},
			async apps(): Promise<AppGrant[]> {
				const [gs, out] = await Promise.all([auth<Auth.Grant[]>("/grants"), auth<Auth.Connection[]>("/connections").catch(() => [] as Auth.Connection[])]);
				return gs.filter((g) => !isHome(g, out)).map((g) => ({ id: String(g.id), name: String(g.instance ?? g.clientId), scopes: g.scopes ?? [], createdAt: when(g.createdAt) }));
			},
			async revokeApp(id) {
				await auth(`/grants/${encodeURIComponent(id)}`, "DELETE");
			},
		},
		connections: {
			list: connections,
			async connect(url) {
				const r = await auth<Auth.ConnectStarted>("/connections", "POST", { remote: url, return_to: returnTo() });
				return { redirect: r.authorize_url ?? null };
			},
			async revoke(id) {
				const c = (await connections()).find((x) => x.id === id);
				await auth(c?.direction === "incoming" ? `/grants/${encodeURIComponent(id)}` : `/connections/${encodeURIComponent(id)}`, "DELETE");
			},
		},
		tokens: {
			async list(): Promise<Token[]> {
				const rows = await auth<Auth.Token[]>("/tokens");
				return rows.map((t) => ({
					id: t.id,
					name: t.name,
					createdAt: when(t.createdAt),
					lastUsedAt: iso(t.lastUsedAt),
					expiresAt: iso(t.expiresAt),
					revoked: t.revokedAt !== undefined,
				}));
			},
			async create(name) {
				const r = await auth<Auth.TokenCreated>("/tokens", "POST", { name });
				return { token: { id: r.id, name, createdAt: new Date().toISOString(), lastUsedAt: null, expiresAt: null, revoked: false }, secret: r.token };
			},
			async revoke(id) {
				await auth(`/tokens/${encodeURIComponent(id)}`, "DELETE");
			},
		},
		blobs: {
			async open(url) {
				const key = url.replace(/^blob:\/\//, "");
				const res = await request(`${base()}/blobs/${key.split("/").map(encodeURIComponent).join("/")}`);
				if (!res.ok) throw new ApiError(`http ${res.status}`, res.status);
				return URL.createObjectURL(await res.blob());
			},
			async put(space, blob) {
				const bytes = await blob.arrayBuffer();
				const sha256 = [...new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))].map((b) => b.toString(16).padStart(2, "0")).join("");
				const res = await request(`${base()}/blobs?space=${encodeURIComponent(space)}&sha256=${sha256}`, { method: "POST", headers: { "content-type": blob.type || "application/octet-stream" }, body: bytes });
				const j = (await res.json().catch(() => null)) as Raw | null;
				if (!res.ok || !j?.key) throw new ApiError(message(j, res.status), res.status);
				return { key: j.key as string, sha256, size: bytes.byteLength };
			},
			async get(key) {
				const res = await request(`${base()}/blobs/${key.split("/").map(encodeURIComponent).join("/")}`);
				if (!res.ok) throw new ApiError(`http ${res.status}`, res.status);
				return res.blob();
			},
		},
		/** Cloudflare's OAuth token endpoint (hq Decision cloudflare-oauth), straight from the browser (the client's CORS
		 *  origins): the grant never passes the instance in plain text. */
		cloudflare: {
			async token(form) {
				const init = { method: "POST", headers: { "content-type": "application/x-www-form-urlencoded" }, body: new URLSearchParams(form).toString() };
				const res = await doFetch("https://dash.cloudflare.com/oauth2/token", init).catch(() => null);
				if (!res) throw new ApiError("offline");
				const j = (await res.json().catch(() => null)) as Raw | null;
				if (!res.ok || !j?.access_token) throw new ApiError(message(j, res.status), res.status);
				return j as { access_token: string; refresh_token?: string; expires_in?: number; scope?: string };
			},
		},
	} satisfies Pick<Api, "auth" | "access" | "connections" | "tokens" | "blobs" | "cloudflare">;

	return {
		...api,
		/** The shared Rust tool table; preview/save never implement templates in the page. */
		async agentTool(name: "graphql", args: Raw): Promise<Raw> {
			const r = await json<Raw>(`${base()}/mcp`, "POST", { jsonrpc: "2.0", id: crypto.randomUUID(), method: "tools/call", params: { name, arguments: args } });
			if (r.error || r.result?.isError) throw new ApiError(r.error?.message ?? r.result.content?.[0]?.text ?? "tool failed");
			return r.result.structuredContent;
		},
		base,
		instance,
		/** An invite link (auth:Decision/invite-links): auth's core makes it at <origin>/auth/invites, truss decides. */
		async invite(space: string, roles: string[]) {
			const j = await json<Auth.InviteCreated | undefined>(`${base()}/auth/invites`, "POST", { space, roles });
			if (!j?.url) throw new ApiError("no invite link");
			return j.url;
		},
		/** The packages the instance offers (its registry). */
		async offered(): Promise<Raw[]> {
			try {
				const r = await doFetch(`${base()}/packages/index.json`);
				return r.ok ? ((await r.json()) as Raw[]) : [];
			} catch {
				return [];
			}
		},
		/** The sealed store (scope = the space id): a value is sealed here by the core (wasm), to public keys. */
		sealed: {
			recipients,
			async save(project: string, profile: string, key: string, value: string) {
				const { seal } = await platformSecrets(WASM);
				let list = await recipients(project);
				for (let attempt = 0; attempt < 2; attempt++) {
					if (!list.length) throw new ApiError("no recipients: register an age key first");
					const ciphertext = seal(JSON.stringify(list), value);
					const res = await request(`${scope(project)}/secrets/${profile}/${key}`, {
						method: "PUT",
						headers: { "content-type": "application/json" },
						body: JSON.stringify({ ciphertext, recipients: list }),
					});
					const j = (await res.json().catch(() => null)) as Raw | null;
					if (res.status === 200) return { rev: Number(j?.rev ?? 0) };
					if (res.status !== 409) throw new ApiError(message(j, res.status), res.status);
					list = strings(j?.recipients);
				}
				throw new ApiError("recipients keep changing", 409);
			},
			async remove(project: string, profile: string, key: string) {
				await json(`${scope(project)}/secrets/${profile}/${key}`, "DELETE");
			},
			read,
			/** Re-seal the scope's stale values to its current recipients: `plan` (the core: answer, recipients, store
			 *  base → PUTs as JSON) decides, this sends. The count sent. */
			async reseal(project: string, plan: (body: string, current: string[], endpoint: string) => string) {
				const [body, current] = await Promise.all([read(project), recipients(project)]);
				const puts = JSON.parse(plan(body, current, `${base()}/secrets`)) as { method: string; url: string; body: string }[];
				for (const p of puts) await json(p.url, p.method, JSON.parse(p.body));
				return puts.length;
			},
		},
	};
}

export type Net = ReturnType<typeof createNet>;
